import React from "react";
import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "microeconomics-sem-1-unit-5-perfect-competition-short-long-run-equilibrium",
  title: "Introductory Microeconomics Unit 5: Perfect Competition, Short & Long Run Equilibrium Notes",
  description: "Master DU Sem 1 Unit 5: Perfect competition assumptions, firm vs industry equilibrium, shutdown point P &lt; AVC, short/long-run supply curve derivation & social efficiency.",
  pubDate: "2025-08-22",
  authorSlug: "uday-kumar",
  category: "subject-explainers",
  tags: ["Perfect Competition", "Market Structures", "Shutdown Point", "Supply Curve", "Microeconomics", "DU Economics"],
  readMinutes: 16,
  heroEyebrow: "Sem 1 · Unit 5 Notes"
};

export function Body() {
  return (
    <article className="prose prose-blue max-w-none">
      <p>
        Welcome to Unit 5 of Introductory Microeconomics for Delhi University Semester 1! In this unit, we shift our focus from consumer and producer behavior to how they interact in the marketplace under <strong>Perfect Competition</strong>. This is a highly foundational chapter, building directly on the cost curves (Unit 4) and revenue concepts you've previously learned.
      </p>
      
      <p>
        This massive, comprehensive guide is designed to act as your complete replacement for reading hefty textbooks, offering clear explanations, real-world Indian examples, step-by-step mathematical derivations, graphical intuition, and past year question frameworks. Let's dive deep into Perfect Competition, Firm vs Industry Equilibrium, Supply Curve derivation, and Efficiency analysis.
      </p>

      <h2>1. Features & Assumptions of Perfect Competition</h2>
      <p>
        Perfect competition is a theoretical market structure. While it rarely exists in its absolute pure form, markets like agricultural commodities (wheat, rice in an APMC mandi) or stock markets come very close. It serves as a benchmark against which all other market structures (Monopoly, Monopolistic Competition, Oligopoly) are judged.
      </p>
      
      <h3>Key Characteristics</h3>
      <ul>
        <li>
          <strong>Large Number of Buyers and Sellers:</strong> The market is highly atomistic. No single buyer or seller is large enough to influence the market price. The share of a single firm's output in the total industry output is mathematically negligible. 
          <em>Result:</em> Every firm is a <strong>Price Taker</strong>. They must accept the prevailing market price determined by industry demand and supply.
        </li>
        <li>
          <strong>Homogeneous Product:</strong> Every firm produces a product that is perfectly identical (perfect substitute) to the product of every other firm in terms of physical characteristics, quality, branding, etc. 
          <em>Result:</em> If a firm raises its price even slightly above the market price, its sales will drop to zero because consumers will instantly switch to competitors.
        </li>
        <li>
          <strong>Free Entry and Exit:</strong> Firms can enter the industry if they see supernormal profits and can leave if they incur losses, without any significant barriers (legal, technical, or financial). This is particularly crucial for Long-Run Equilibrium.
        </li>
        <li>
          <strong>Perfect Knowledge:</strong> Both buyers and sellers have complete information about prices, technology, and costs. There are no information asymmetries.
        </li>
        <li>
          <strong>Perfect Mobility of Factors of Production:</strong> Labor and capital can move freely between firms and industries in search of the highest returns.
        </li>
        <li>
          <strong>Absence of Transport Costs:</strong> Assumed to simplify the model, ensuring the price remains identical everywhere.
        </li>
      </ul>

      <h3>The Firm's Demand Curve (Price Taker Condition)</h3>
      <p>
        Because the firm is a price taker, it can sell any quantity of output at the prevailing market price, say P*. 
        Therefore, the demand curve facing an individual firm is perfectly elastic (a horizontal straight line) at the market price level.
      </p>
      <p>
        Mathematically, Total Revenue (TR) = P * Q. <br />
        Average Revenue (AR) = TR / Q = (P * Q) / Q = P. <br />
        Marginal Revenue (MR) = d(TR) / dQ = P (since P is constant).
      </p>
      <p>
        <strong>Conclusion:</strong> For a perfectly competitive firm, P = AR = MR. The horizontal demand curve is also the AR and MR curve.
      </p>

      <h2>2. Short-Run Equilibrium of a Competitive Firm</h2>
      <p>
        In the short run, at least one factor of production (usually capital) is fixed. Firms cannot enter or exit the industry; the number of firms is fixed. The firm’s objective is to maximize its economic profit (Pi), where Pi = TR - TC.
      </p>

      <h3>Profit Maximization Rules (MR-MC Approach)</h3>
      <p>
        Instead of looking at total curves, economists prefer marginal analysis. The firm achieves equilibrium (maximum profit) when two conditions are met:
      </p>
      <ol>
        <li><strong>Necessary Condition (First Order):</strong> MR = MC. If MR &gt; MC, producing an extra unit adds more to revenue than to cost, increasing profit. If MR &lt; MC, producing an extra unit decreases profit. Hence, profits are maximized when MR = MC. Since MR = P, the rule becomes P = MC.</li>
        <li><strong>Sufficient Condition (Second Order):</strong> MC curve must cut the MR curve from below. This means MC must be rising at the point of equilibrium (Slope of MC &gt; 0). If MC cuts MR from above, it's a point of profit minimization.</li>
      </ol>

      <h3>Three Short-Run Scenarios</h3>
      <p>Depending on the position of the Short-Run Average Cost (SAC) relative to Price (AR), a firm can face three situations in the short run:</p>
      <ul>
        <li>
          <strong>Supernormal (Economic) Profit:</strong> If at equilibrium quantity Q*, the Price (P*) &gt; SAC. 
          The firm covers all its explicit and implicit costs and makes an extra profit. 
          Profit = (AR - SAC) * Q*. The profit area is a rectangle between the price line and SAC curve.
        </li>
        <li>
          <strong>Normal Profit (Zero Economic Profit):</strong> If at Q*, Price (P*) = SAC. 
          The firm just covers its total economic costs. TR = TC. The SAC curve is exactly tangent to the AR=MR line at the minimum point of SAC.
        </li>
        <li>
          <strong>Subnormal Profit (Economic Loss):</strong> If at Q*, Price (P*) &lt; SAC. 
          The firm's revenue is less than its total cost. Loss = (SAC - AR) * Q*. 
          But does the firm shut down immediately? Not necessarily! This leads us to the shutdown point.
        </li>
      </ul>

      <h2>3. Shutdown Decision & Derivation of Short-Run Supply Curve</h2>
      <p>
        A key concept in DU exams is the firm's decision to continue producing or shut down when making losses.
      </p>

      <h3>The Shutdown Rule</h3>
      <p>
        In the short run, Total Cost (TC) = Total Fixed Cost (TFC) + Total Variable Cost (TVC).
        Even if the firm produces zero units (shuts down), it still has to pay TFC (rent, insurance, etc.). These are often considered "sunk costs" in the short run.
        Therefore, if the firm shuts down, its Loss = TFC.
      </p>
      <p>
        If the firm produces, its Loss = TC - TR = (TFC + TVC) - TR.
        The firm will choose the option that <em>minimizes</em> its losses.
        It will continue to produce as long as TR &gt; TVC, or dividing by Q, as long as P &gt; AVC (Average Variable Cost).
      </p>
      <p>
        <strong>The Shutdown Point:</strong> This occurs where Price equals the minimum of Average Variable Cost (P = min AVC). 
        At this point, TR = TVC. The firm is indifferent between producing and shutting down because its loss equals TFC in both cases. 
        If price falls even a fraction of a rupee below min AVC, the firm shuts down, producing Q = 0.
      </p>

      <h3>Deriving the Short-Run Supply Curve</h3>
      <p>
        The firm's supply curve shows the quantity it will supply at various prices. 
        Since the firm always sets P = MC (as long as it produces), the MC curve tells us the quantity supplied at any given price.
      </p>
      <p>
        However, the firm will not supply anything if P &lt; min AVC.
        <strong>Conclusion:</strong> The short-run supply curve of a perfectly competitive firm is the portion of its Marginal Cost (MC) curve that lies above the minimum point of the Average Variable Cost (AVC) curve. For prices below min AVC, the quantity supplied is zero.
      </p>

      <h2>4. Industry Short-Run Supply Curve</h2>
      <p>
        The short-run market (industry) supply curve is derived by taking the horizontal summation of the short-run supply curves (the MC curves above min AVC) of all individual firms in the industry.
      </p>
      <p>
        If there are 100 identical firms, and at Price = Rs 20, one firm supplies 50 units, the industry supplies 100 * 50 = 5000 units.
        Mathematically: Q_industry(P) = Summation(i=1 to n) [q_i(P)].
      </p>
      <p>
        The industry demand curve is downward sloping (Law of Demand). 
        The intersection of the industry demand curve and the industry short-run supply curve determines the short-run equilibrium market price (P*).
      </p>

      <h2>5. Long-Run Equilibrium of Firm & Industry</h2>
      <p>
        In the long run, all factors of production are variable. Firms can alter their plant size, and crucially, new firms can enter the industry or existing ones can leave.
      </p>

      <h3>The Adjustment Process (Entry and Exit)</h3>
      <p>
        If firms in the industry are making <strong>supernormal profits</strong> in the short run, this acts as a magnet for new firms. 
        As new firms enter, the industry supply curve shifts to the right. This increases total market quantity but drives the market price down.
        The price will continue to fall until supernormal profits are wiped out, i.e., until P = min LAC (Long-Run Average Cost).
      </p>
      <p>
        Conversely, if firms are making <strong>losses</strong>, some firms will exit the industry over time. 
        The industry supply curve shifts left, driving the market price up, until the remaining firms are making normal profits.
      </p>

      <h3>Long-Run Equilibrium Conditions</h3>
      <p>For the industry to be in long-run equilibrium, every firm must be making exactly zero economic profit (normal profit). The conditions are:</p>
      <ol>
        <li>Firms maximize long-run profit: P = LMC.</li>
        <li>Firms earn zero economic profit: P = LAC.</li>
        <li>Therefore, P = LMC = LAC. Since LMC intersects LAC at its minimum point, the firm operates at the minimum efficient scale.</li>
      </ol>
      <p>
        Furthermore, in the long run, the firm's short-run curves also align perfectly. 
        The grand equation for Long-Run Equilibrium of a perfectly competitive firm is:
        <strong>P = MR = AR = LMC = LAC = SMC = SAC</strong>
        (where SMC and SAC are the Short-Run Marginal and Average Cost curves for the optimal plant size).
      </p>

      <h3>Long-Run Industry Supply Curve (LS)</h3>
      <p>
        The shape of the long-run industry supply curve depends on how input prices react when the entire industry expands (due to new firm entry). We have three cases:
      </p>
      <ul>
        <li>
          <strong>Constant Cost Industry:</strong> As the industry expands, input prices (like raw materials, labor wages) remain unchanged. The new firms have the exact same cost curves as the old ones. The long-run supply (LS) curve is a horizontal line at the price equal to min LAC.
        </li>
        <li>
          <strong>Increasing Cost Industry:</strong> (Most common). As the industry expands, the increased demand for inputs drives up input prices. 
          Cost curves of all firms shift upward. The new equilibrium price must be higher to cover the higher minimum LAC. The LS curve is upward sloping. Example: Agricultural land becoming scarce as farming expands.
        </li>
        <li>
          <strong>Decreasing Cost Industry:</strong> As the industry expands, it creates positive external economies of scale (e.g., a localized hub forms, leading to cheaper specialized labor or better infrastructure). Input prices fall, shifting cost curves down. The LS curve is downward sloping. Example: Early days of the tech hardware industry.
        </li>
      </ul>

      <h2>6. Efficiency Analysis: Why Perfect Competition is "Perfect"</h2>
      <p>
        Economists love perfect competition because, under certain assumptions, it leads to the optimal allocation of society's scarce resources.
      </p>
      
      <h3>Allocative Efficiency (P = MC)</h3>
      <p>
        Allocative efficiency occurs when resources are allocated to produce the exact mix of goods that society values most. 
        Price (P) represents the marginal benefit to society of consuming one more unit. 
        Marginal Cost (MC) represents the opportunity cost to society of producing that unit.
        In perfect competition, since firms set P = MC, the marginal benefit equals marginal cost. No one can be made better off without making someone else worse off (Pareto Optimality).
      </p>

      <h3>Productive Efficiency (P = min LAC)</h3>
      <p>
        Productive efficiency occurs when goods are produced at the lowest possible cost. 
        In long-run perfect competition, firms are forced to produce at the minimum point of their Long-Run Average Cost (LAC) curve to survive. No resources are wasted.
      </p>

      <h3>Maximization of Total Social Surplus</h3>
      <p>
        Total Surplus = Consumer Surplus (CS) + Producer Surplus (PS).
        In a perfectly competitive market left to itself (no government intervention, no externalities), the equilibrium price and quantity maximize the total surplus area. Any interference (like a price floor or ceiling) will create a Deadweight Loss (DWL).
      </p>

      <h2>7. Impact of Taxes & Subsidies</h2>
      <p>Understanding how government intervention affects the competitive equilibrium is a favorite DU exam topic.</p>

      <h3>Per-Unit Tax (Specific Tax)</h3>
      <ul>
        <li><strong>Short Run:</strong> A tax of 't' per unit shifts the firm's MC and AVC curves upward by 't'. 
        The industry supply curve shifts upward by 't'. 
        Result: Market price rises (but usually by less than 't'), quantity exchanged falls. Consumer and Producer surplus fall, creating deadweight loss. 
        Firms may make short-run losses.</li>
        <li><strong>Long Run:</strong> In a constant cost industry, the long-run supply curve shifts up by exactly 't'. The entire burden of the tax is passed on to consumers. Price rises by 't', firms return to normal profit (zero economic profit), and industry output shrinks as some firms exit.</li>
      </ul>

      <h3>Lump-Sum Tax</h3>
      <ul>
        <li><strong>Short Run:</strong> A fixed lump-sum tax (like a license fee) affects only Total Fixed Cost (TFC) and Average Fixed Cost (AFC). 
        It does NOT change Marginal Cost (MC) or Average Variable Cost (AVC). 
        Therefore, the firm's short-run profit-maximizing quantity (where P = MC) remains completely unchanged. 
        The firm just absorbs the tax out of its profits. It does not affect the industry supply or price in the short run.</li>
        <li><strong>Long Run:</strong> The lump-sum tax increases Average Cost (AC). 
        Firms now make losses and exit the industry. Industry supply shifts left, raising the market price until remaining firms cover the higher AC. The lump-sum tax acts like a fixed cost that eventually gets passed to consumers.</li>
      </ul>

      <h2>8. Fully Solved Numericals for DU Exams</h2>
      <p>Let's tackle the heavy-hitting numericals that carry 6-8 marks in your final exams.</p>

      <h3>Numerical 1: Profit Maximization and Shutdown Point</h3>
      <div className="bg-gray-100 p-4 rounded-md my-4 font-mono text-sm">
        <p><strong>Question:</strong> A perfectly competitive firm has a Total Cost function given by: TC = 0.5Q^2 + 10Q + 50. The market price is P = 40.<br/>
        a) Find the profit-maximizing output (Q*).<br/>
        b) Calculate the maximum profit.<br/>
        c) Find the minimum price required for the firm to avoid shutting down in the short run.</p>
        
        <p><strong>Solution:</strong></p>
        <p><strong>Part a) Output (Q*)</strong><br/>
        Rule for profit maximization: P = MC.<br/>
        First, find MC by differentiating TC with respect to Q:<br/>
        MC = d(TC)/dQ = d(0.5Q^2 + 10Q + 50)/dQ = 1.0Q + 10 = Q + 10.<br/>
        Set P = MC:<br/>
        40 = Q + 10<br/>
        Q* = 30 units.<br/>
        (Check second order: Slope of MC is d(MC)/dQ = 1 &gt; 0. So it's a maximum).</p>

        <p><strong>Part b) Maximum Profit</strong><br/>
        Profit = TR - TC.<br/>
        TR = P * Q = 40 * 30 = 1200.<br/>
        TC = 0.5(30)^2 + 10(30) + 50 = 0.5(900) + 300 + 50 = 450 + 350 = 800.<br/>
        Profit = 1200 - 800 = 400 (Supernormal profit).</p>

        <p><strong>Part c) Shutdown Price</strong><br/>
        The shutdown price is the minimum of Average Variable Cost (AVC).<br/>
        From TC = 0.5Q^2 + 10Q + 50:<br/>
        TFC = 50 (term without Q).<br/>
        TVC = 0.5Q^2 + 10Q.<br/>
        AVC = TVC / Q = (0.5Q^2 + 10Q) / Q = 0.5Q + 10.<br/>
        To find min AVC, we set d(AVC)/dQ = 0, but here it's a linear upward sloping line starting at Q=0. The minimum AVC theoretically occurs as Q approaches 0. At Q=0, AVC = 10.<br/>
        Alternatively, MC = AVC at min AVC:<br/>
        Q + 10 = 0.5Q + 10 =&gt; 0.5Q = 0 =&gt; Q = 0.<br/>
        Substitute Q=0 into AVC: AVC = 0.5(0) + 10 = 10.<br/>
        Therefore, Shutdown Price = 10.</p>
      </div>

      <h3>Numerical 2: Industry Equilibrium</h3>
      <div className="bg-gray-100 p-4 rounded-md my-4 font-mono text-sm">
        <p><strong>Question:</strong> In a perfectly competitive market, there are 100 identical firms. Each firm's short-run MC curve is given by MC = 4 + 2q (where q is firm output). 
        The market demand curve is Qd = 1000 - 50P.
        a) Find the firm's supply curve and the industry supply curve.
        b) Determine the short-run equilibrium market price and total industry output.
        c) Determine the output of each individual firm.</p>

        <p><strong>Solution:</strong></p>
        <p><strong>Part a) Supply Curves</strong><br/>
        Firm's supply curve is given by P = MC (assuming P &gt; min AVC).<br/>
        P = 4 + 2q.<br/>
        Express q in terms of P to get the firm's supply function:<br/>
        2q = P - 4<br/>
        q = (P/2) - 2. (This is valid for P &gt; 4, since q must be positive).<br/>
        Industry supply (Qs) is the sum of all 100 firms' supply:<br/>
        Qs = 100 * q = 100 * ((P/2) - 2)<br/>
        Qs = 50P - 200.</p>

        <p><strong>Part b) Equilibrium Price and Output</strong><br/>
        Equilibrium occurs where Qd = Qs.<br/>
        1000 - 50P = 50P - 200<br/>
        1200 = 100P<br/>
        P* = 12 (Market Price).<br/>
        Substitute P=12 into the Demand or Supply equation to get Q*:<br/>
        Q* = 1000 - 50(12) = 1000 - 600 = 400 (Total Industry Output).</p>

        <p><strong>Part c) Individual Firm Output</strong><br/>
        Each firm produces q = Q* / 100 = 400 / 100 = 4 units.<br/>
        Alternatively, plug P=12 into the firm's supply curve:<br/>
        q = (12/2) - 2 = 6 - 2 = 4 units.</p>
      </div>

      <h3>Numerical 3: Long-Run Adjustment</h3>
      <div className="bg-gray-100 p-4 rounded-md my-4 font-mono text-sm">
        <p><strong>Question:</strong> An industry has identical firms with Long-Run Average Cost given by: LAC = q^2 - 10q + 40. 
        The market demand is Qd = 600 - 10P.<br/>
        a) Find the long-run equilibrium price.<br/>
        b) Find the quantity produced by each firm in the long run.<br/>
        c) How many firms will be in the industry in the long run?</p>

        <p><strong>Solution:</strong></p>
        <p><strong>Part a & b) Price and Firm Quantity</strong><br/>
        In the long run, firms produce at the minimum point of their LAC curve, where P = min LAC.<br/>
        To find min LAC, take the derivative of LAC with respect to q and set it to 0:<br/>
        d(LAC)/dq = 2q - 10 = 0<br/>
        2q = 10<br/>
        q* = 5 units (This is the long-run output of each individual firm).<br/>
        Now, plug q=5 back into the LAC equation to find the minimum AC, which will be the long-run price:<br/>
        LAC = (5)^2 - 10(5) + 40 = 25 - 50 + 40 = 15.<br/>
        So, P* = 15.</p>

        <p><strong>Part c) Number of Firms</strong><br/>
        First, find the total industry demand at the long-run price P=15:<br/>
        Qd = 600 - 10(15) = 600 - 150 = 450 units.<br/>
        If total industry output is 450, and each firm produces 5 units, the number of firms (N) is:<br/>
        N = Total Quantity / Firm Quantity = 450 / 5 = 90 firms.<br/>
        There will be 90 firms in the industry in long-run equilibrium.</p>
      </div>

      <h2>9. DU Exam Preparation & Frameworks</h2>
      <p>
        Unit 5 is heavy on diagrams and numericals. The standard theory question is often 10-15 marks and requires a structured approach.
      </p>

      <h3>15-Mark Question Framework: "Explain the Long-Run Equilibrium of a Competitive Firm and Industry"</h3>
      <ol>
        <li><strong>Introduction (1-2 mins):</strong> Define perfect competition and explicitly list the 3 core assumptions relevant here (Price taker, identical products, free entry/exit).</li>
        <li><strong>The Concept of Normal Profit (2 mins):</strong> Define zero economic profit. Explain why this is the only sustainable state in the long run (due to the entry/exit assumption).</li>
        <li><strong>Firm Equilibrium Diagram (4 mins):</strong> Draw the firm's graph. X-axis: firm quantity (q), Y-axis: Cost/Price. Draw the horizontal P=MR=AR line. Draw a U-shaped LAC curve tangent to the P line at its minimum. Draw LMC passing through the minimum of LAC. Show that P = LMC = LAC.</li>
        <li><strong>Industry Equilibrium Diagram (4 mins):</strong> Side-by-side with the firm diagram, draw the industry graph. X-axis: Market Quantity (Q), Y-axis: Price. Draw downward sloping Market Demand (D) and upward sloping Market Supply (S) intersecting exactly at the Price level P drawn in the firm's diagram.</li>
        <li><strong>Explanation of Adjustment Mechanism (3 mins):</strong> Write a strong paragraph explaining <em>how</em> equilibrium is reached. If P &gt; LAC (supernormal profit) -&gt; firms enter -&gt; S shifts right -&gt; P falls -&gt; profits drop to normal. If P &lt; LAC (loss) -&gt; firms exit -&gt; S shifts left -&gt; P rises -&gt; losses are eliminated.</li>
      </ol>

      <h3>Common Diagram Mistakes to Avoid</h3>
      <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md my-4">
        <ul className="list-disc ml-4 text-red-900">
          <li><strong>MC not cutting AC at the minimum:</strong> This is a fatal error. Your Marginal Cost curve MUST intersect both the Average Variable Cost (AVC) and Average Total Cost (AC/SAC/LAC) curves exactly at their lowest points. DU evaluators look for this specifically.</li>
          <li><strong>Confusing Q (Industry Output) and q (Firm Output):</strong> Always label the X-axis for the Industry diagram as capital 'Q' and the Firm diagram as lowercase 'q'. A firm produces 50 units, the industry produces 50,000 units. They are not on the same scale.</li>
          <li><strong>Drawing downward sloping MR for perfect competition:</strong> P=AR=MR is a horizontal straight line. Downward sloping MR is for Monopoly (Unit 6).</li>
        </ul>
      </div>

      <p>
        <strong>Final Study Tip:</strong> Practice deriving the short-run supply curve with a blank sheet of paper. Be able to confidently explain why the firm ignores fixed costs (sunk costs) in the short run and focuses only on covering variable costs. Mastering this unit makes the upcoming Monopoly unit a breeze, as you will constantly be comparing Monopoly outcomes against the Perfect Competition benchmark!
      </p>

    </article>
  );
}



